import _ from 'lodash';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { SessionState, RootState, portalAccessToken } from '@/store/types';
import JwtDecode from 'jwt-decode';
import axios from '@/utils/axios';
import router from '@/router';
import { clearErrors, loginValidationSchema, validateSchema } from '@/utils/validation';
import { refNoToModuleId } from '@/utils/utils';

export const state: SessionState = {
    portalAccessToken: '',
    recordId: 0,
    refNo: '',
    errors: {},
    isVerifying: false,
};

export const getters: GetterTree<SessionState, RootState> = {
    getFieldErrors: state => (field: string) => state.errors[field],
    isFormValid: state => _.isEmpty(state.errors),
};

export const mutations: MutationTree<SessionState> = {
    setPortalAccessToken(state, payload) {
        state.portalAccessToken = payload.portalAccessToken;
    },
    setRecordId(state, payload) {
        state.recordId = payload.recordId;
    },
    setRefNo(state, payload) {
        state.refNo = payload.refNo;
    },
    setIsVerifying(state, payload) {
        state.isVerifying = payload.isVerifying;
    },
    setErrors(state, payload) {
        state.errors = payload.errors;
    },
    clearError(state, payload) {
        state.errors = clearErrors(state.errors, payload.field);
    },
};

export const actions: ActionTree<SessionState, RootState> = {
    async verify({ commit, dispatch, rootState }, payload) {
        const errors = await validateSchema(loginValidationSchema, payload);
        if (errors) {
            return commit('setErrors', { errors });
        }
        commit('setIsVerifying', { isVerifying: true });
        try {
            const response = await axios.post(
                `/${rootState.instanceId}/portal_data/${refNoToModuleId(payload.refNo)}/item/verify/${payload.refNo}`,
                {
                    password: payload.password,
                },
            );
            if (response.data?.success === false) {
                const message = response.data?.message;
                commit('setErrors', { errors: { global: [message] } });
                return;
            }
            await dispatch('logIn', { portalAccessToken: response.data.data.portalAccessToken });
        } catch (err) {
            const message = err.response?.data?.message;
            commit('setErrors', { errors: { global: [message] } });
        } finally {
            commit('setIsVerifying', { isVerifying: false });
        }
    },
    async refresh({ dispatch, rootState }) {
        const response = await axios.get(`/${rootState.instanceId}/portal_refresh`);
        await dispatch('logIn', { portalAccessToken: response.data.data.portalAccessToken });
    },
    logIn({ commit, dispatch }, payload) {
        const decoded = JwtDecode<portalAccessToken>(payload.portalAccessToken);
        commit('setPortalAccessToken', { portalAccessToken: payload.portalAccessToken });
        commit('setRecordId', { recordId: decoded.recordID });
        commit('setRefNo', { refNo: decoded.refNo });
        // set a timeout to refresh token 30 seconds before expiration
        const secondsBeforeExpiration = payload.__test__secondsBeforeExpiration__ || 30;
        const timeout = decoded.exp * 1000 - new Date().getTime() - secondsBeforeExpiration * 1000;
        setTimeout(() => {
            dispatch('refresh');
        }, timeout);
    },
    async logOut({ commit, rootState, state }) {
        await axios.get(`/${rootState.instanceId}/portal_logout`);
        if (!state.portalAccessToken) {
            return;
        }
        commit('setPortalAccessToken', { portalAccessToken: '' });
        commit('setRecordId', { recordId: 0 });
        commit('setRefNo', { refNo: '' });
        await router.push({ name: 'VerifyPage', params: { instanceId: rootState.instanceId } });
    },
};

const SessionStore: Module<SessionState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default SessionStore;
